import {
  LayoutDashboard,
  Layers,
  Camera,
  Ruler,
  FileText,
  ClipboardList,
  Users,
  Wrench,
  FileSearch,
  ShieldAlert,
  PackageCheck,
  Package,
} from "lucide-react";
import { GC_VLAD_ORG_ID } from "@/lib/gcSubtrades";
import { useAuthContext } from "@/contexts/AuthContext";

interface JobTabsProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  jobType?: string | null;
}

const TAB_META: Record<string, { label: string; icon: any }> = {
  "Overview": { label: "Overview", icon: LayoutDashboard },
  "Trades": { label: "Trades", icon: Layers },
  "Photo Hub": { label: "Photos", icon: Camera },
  "Measurement/Estimate": { label: "Estimate", icon: Ruler },
  "Quick Estimate": { label: "Estimate", icon: Ruler },
  "Proposals": { label: "Proposals", icon: FileText },
  "Supplements": { label: "Supplements", icon: ShieldAlert },
  "Claim Reader": { label: "Claim", icon: FileSearch },
  "Forms": { label: "Forms", icon: ClipboardList },
  "Team & Activity": { label: "Team", icon: Users },
  "Contingency Forms": { label: "Docs", icon: FileText },
  "Production": { label: "Production", icon: PackageCheck },
  "Materials": { label: "Materials", icon: Package },
};

const getTabsForJobType = (jobType?: string | null) => {
  if (jobType === "Insurance") {
    return [
      "Overview",
      "Trades",
      "Photo Hub",
      "Measurement/Estimate",
      "Supplements",
      "Claim Reader",
      "Forms",
      "Team & Activity",
      "Contingency Forms",
      "Production",
    ];
  }
  if (jobType === "Commercial") {
    return [
      "Overview",
      "Trades",
      "Photo Hub",
      "Quick Estimate",
      "Proposals",
      "Forms",
      "Team & Activity",
      "Production",
    ];
  }
  return [
    "Overview",
    "Trades",
    "Photo Hub",
    "Measurement/Estimate",
    "Proposals",
    "Forms",
    "Team & Activity",
    "Production",
  ];
};

export const JobTabs = ({ activeTab, onTabChange, jobType }: JobTabsProps) => {
  const { orgId } = useAuthContext();
  const showMaterials = orgId === GC_VLAD_ORG_ID;
  const tabs = [
    ...getTabsForJobType(jobType),
    ...(showMaterials ? ["Materials"] : []),
  ];
  return (
    <div className="bg-card/80 backdrop-blur-sm border-b border-border/60 sticky top-0 z-10">
      <div className="w-full px-3 sm:px-4 py-2.5">
        <div className="flex items-center w-full gap-1.5 overflow-x-auto">
          {tabs.map((tab) => {
            const meta = TAB_META[tab] ?? { label: tab, icon: Wrench };
            const Icon = meta.icon;
            const isActive = activeTab === tab;
            return (
              <button
                key={tab}
                onClick={() => onTabChange(tab)}
                title={tab}
                className={`group flex-1 min-w-0 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs lg:text-sm font-semibold rounded-lg border transition-all duration-150 whitespace-nowrap ${
                  isActive
                    ? "bg-primary text-primary-foreground border-primary shadow-md shadow-primary/30 scale-[1.02]"
                    : "bg-muted/40 text-muted-foreground border-border/60 hover:bg-muted hover:text-foreground hover:border-border"
                }`}
              >
                <Icon className={`h-3.5 w-3.5 lg:h-4 lg:w-4 shrink-0 ${isActive ? "" : "opacity-70 group-hover:opacity-100"}`} />
                <span className="truncate">{meta.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
